import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Info, Heart, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CONFIG = {
  org_name: "Melbourne Grand Mosque",
  currency: "AUD",
  purposes: [],
  preset_amounts: [20, 50, 100, 200],
  min_amount: 1,
  max_amount: 100000,
};

export default function DonatePage() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [purposeId, setPurposeId] = useState("");
  const [monthly, setMonthly] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/donation-config`)
      .then((res) => {
        setConfig(res.data);
        if (res.data?.purposes?.length > 0) {
          setPurposeId(res.data.purposes[0].id);
        }
      })
      .catch(() => {
        toast.error("Could not load donation config");
      });
  }, []);

  const effectiveAmount = customAmount
    ? parseFloat(customAmount)
    : selectedAmount;

  const handleSelectAmount = (amt) => {
    setSelectedAmount(amt);
    setCustomAmount("");
  };

  const handleCustomAmount = (e) => {
    const v = e.target.value.replace(/[^0-9.]/g, "");
    setCustomAmount(v);
    setSelectedAmount(null);
  };

  const handleDonate = async () => {
    if (!purposeId) {
      toast.error("Please select a donation purpose");
      return;
    }
    if (!effectiveAmount || effectiveAmount < config.min_amount) {
      toast.error(`Minimum amount is ${config.currency} ${config.min_amount}`);
      return;
    }
    if (effectiveAmount > config.max_amount) {
      toast.error(`Maximum amount is ${config.currency} ${config.max_amount}`);
      return;
    }

    setLoading(true);
    try {
      // Use top-level origin when inside iframe so success/cancel return there too.
      let origin = window.location.origin;
      try {
        if (window.top && window.top !== window.self) {
          if (document.referrer) {
            origin = new URL(document.referrer).origin;
          }
        }
      } catch (e) {
        // cross-origin, fall back to our own origin
      }

      const { data } = await axios.post(`${API}/checkout/session`, {
        purpose_id: purposeId,
        amount: parseFloat(effectiveAmount),
        monthly,
        donor_name: donorName || undefined,
        donor_email: donorEmail || undefined,
        origin_url: window.location.origin, // success/cancel back to our hosted pages
      });

      if (data?.url) {
        // Break out of iframe (Stripe blocks framing) – navigate top window
        try {
          window.top.location.href = data.url;
        } catch (err) {
          window.location.href = data.url;
        }
      } else {
        toast.error("No checkout URL returned");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Unable to start checkout";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full"
      style={{ backgroundColor: "#E8E6F5" }}
      data-testid="donate-widget"
    >
      <div className="max-w-md mx-auto px-5 pt-7 pb-12">
        {/* Heading */}
        <div className="mb-6">
          <h1
            className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight"
            data-testid="donate-heading"
          >
            Donate
          </h1>
          <p className="text-slate-500 text-base mt-1">
            Support {config.org_name}
          </p>
        </div>

        {/* About donations accordion */}
        <Accordion
          type="single"
          collapsible
          className="bg-white rounded-2xl shadow-sm border border-white"
          data-testid="about-donations-accordion"
        >
          <AccordionItem value="about" className="border-b-0">
            <AccordionTrigger
              className="px-4 py-3.5 hover:no-underline"
              data-testid="about-donations-trigger"
            >
              <span className="flex items-center gap-2.5 text-slate-900 font-semibold">
                <Info className="w-4 h-4 text-slate-600" strokeWidth={2.5} />
                About donations
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 text-slate-600 text-sm leading-relaxed">
              Your donation directly supports {config.org_name}. One-time
              contributions are processed securely via Stripe. Monthly
              donations can be cancelled anytime from your receipt email. All
              amounts are in {config.currency}.
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Purpose selector */}
        <div className="mt-8">
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">
            What supports your donation?
          </p>
          <div className="grid grid-cols-1 gap-2.5" role="radiogroup">
            {config.purposes.map((p) => {
              const isSelected = purposeId === p.id;
              return (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setPurposeId(p.id)}
                  data-testid={`purpose-option-${p.id}`}
                  aria-checked={isSelected}
                  role="radio"
                  className={
                    "flex items-start text-left p-4 rounded-2xl transition-all duration-200 shadow-sm " +
                    (isSelected
                      ? "bg-[#EFEBFA] border-2 border-[#0F172A] shadow-md"
                      : "bg-white border-2 border-transparent hover:shadow-md")
                  }
                >
                  <span
                    className={
                      "mt-0.5 flex items-center justify-center w-5 h-5 rounded-full border-2 shrink-0 " +
                      (isSelected
                        ? "border-[#0F172A] bg-[#0F172A]"
                        : "border-slate-300 bg-white")
                    }
                  >
                    {isSelected && (
                      <span className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </span>
                  <span className="ml-3 flex-1">
                    <span className="block font-heading text-slate-900 font-bold text-base sm:text-lg leading-tight">
                      {p.title}
                    </span>
                    <span className="block text-slate-500 text-sm mt-0.5">
                      {p.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Monthly toggle */}
        <div
          className="mt-4 bg-white rounded-2xl shadow-sm p-4 flex items-center justify-between"
          data-testid="monthly-toggle-wrapper"
        >
          <div className="pr-3">
            <p className="font-heading font-bold text-slate-900 text-base sm:text-lg">
              Monthly Donation
            </p>
            <p className="text-slate-500 text-sm leading-snug mt-0.5">
              Set up an automatic monthly contribution. Cancel anytime.
            </p>
          </div>
          <Switch
            checked={monthly}
            onCheckedChange={setMonthly}
            data-testid="monthly-toggle"
            className="data-[state=checked]:bg-[#0F172A]"
          />
        </div>

        {/* Amount selector */}
        <div className="mt-8">
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-3">
            Select amount ({config.currency})
          </p>
          <div className="grid grid-cols-2 gap-3">
            {config.preset_amounts.map((amt) => {
              const isSelected = selectedAmount === amt && !customAmount;
              return (
                <button
                  type="button"
                  key={amt}
                  onClick={() => handleSelectAmount(amt)}
                  data-testid={`amount-tile-${amt}`}
                  className={
                    "py-6 rounded-2xl font-heading font-extrabold text-2xl transition-all duration-200 shadow-sm " +
                    (isSelected
                      ? "bg-[#EFEBFA] border-2 border-[#0F172A] text-slate-900 shadow-md"
                      : "bg-white border-2 border-transparent text-slate-900 hover:shadow-md")
                  }
                >
                  ${amt}
                </button>
              );
            })}
          </div>

          {/* Custom amount */}
          <div className="relative mt-3">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-lg">
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="Other amount"
              value={customAmount}
              onChange={handleCustomAmount}
              data-testid="custom-amount-input"
              className="w-full pl-8 pr-4 py-4 bg-white rounded-2xl shadow-sm text-lg font-bold text-slate-900 placeholder:text-slate-400 placeholder:font-medium focus:outline-none focus:ring-2 focus:ring-[#0F172A] border-none"
            />
          </div>
        </div>

        {/* Donor details */}
        <div className="mt-6 grid grid-cols-1 gap-3">
          <input
            type="text"
            placeholder="Your name (optional)"
            value={donorName}
            onChange={(e) => setDonorName(e.target.value)}
            data-testid="donor-name-input"
            className="w-full px-4 py-3.5 bg-white rounded-xl shadow-sm text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F172A] border-none"
          />
          <input
            type="email"
            placeholder="Email for receipt (optional)"
            value={donorEmail}
            onChange={(e) => setDonorEmail(e.target.value)}
            data-testid="donor-email-input"
            className="w-full px-4 py-3.5 bg-white rounded-xl shadow-sm text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F172A] border-none"
          />
        </div>

        {/* Summary + CTA */}
        <div className="mt-7 bg-white/70 backdrop-blur rounded-2xl p-4 flex items-center justify-between border border-white">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">
              You're donating
            </p>
            <p className="font-heading text-2xl font-extrabold text-slate-900 mt-0.5">
              ${effectiveAmount ? Number(effectiveAmount).toFixed(2) : "0.00"}{" "}
              <span className="text-base font-bold text-slate-500">
                {config.currency}
              </span>
              {monthly && (
                <span className="text-base font-bold text-slate-500">
                  {" "}
                  / month
                </span>
              )}
            </p>
          </div>
          <Heart className="w-7 h-7 text-[#0F172A]" strokeWidth={2.5} />
        </div>

        <button
          type="button"
          onClick={handleDonate}
          disabled={loading || !purposeId || !effectiveAmount}
          data-testid="donate-submit-button"
          className="mt-5 w-full py-4 px-6 bg-[#0F172A] hover:bg-[#1E293B] disabled:opacity-60 disabled:cursor-not-allowed text-white text-lg font-heading font-bold rounded-2xl shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Redirecting…
            </>
          ) : (
            <>
              <Check className="w-5 h-5" strokeWidth={3} />
              Donate {monthly ? "Monthly" : "Now"}
            </>
          )}
        </button>

        <p className="text-center text-xs text-slate-500 mt-4 leading-relaxed">
          Payments secured by Stripe. You'll be redirected to complete your
          donation.
        </p>
      </div>
    </div>
  );
}
